import { useEffect, useId, useState } from 'react';
import { Download, Trash2, Type, Upload } from 'lucide-react';

import { fontsApi } from '../../api/endpoints';
import { ErrorBanner } from '../../components/ErrorBanner';
import {
  Button,
  Card,
  CardHead,
  ConfirmDialog,
  EmptyState,
  Input,
  Pill,
  Select,
  SkeletonRows,
} from '../../components/ui';
import {
  useCurrentUser,
  useDeleteFont,
  useFontTeams,
  useFonts,
  useUpdateFont,
  useUploadFonts,
} from '../../hooks';
import { SHARED_TEAM, teamLabel } from '../../lib/team';
import type { Font, FontUploadResult } from '../../types/api';
import styles from './Fonts.module.css';

// Two scripts by default: a font without Cyrillic still shows its own letters in the Latin line.
const DEFAULT_SAMPLES = [
  'Съешь же ещё этих мягких французских булок',
  'The quick brown fox jumps over the lazy dog',
];

function TeamOptions({ teams }: { teams: string[] }) {
  return (
    <>
      <option value={SHARED_TEAM}>{teamLabel(SHARED_TEAM)}</option>
      {teams.map((team) => (
        <option key={team} value={team}>
          {team}
        </option>
      ))}
    </>
  );
}

function sizeLabel(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`
    : `${Math.max(1, Math.round(bytes / 1024))} КБ`;
}

// One load per font for the whole page: rows remount and effects may run twice.
const loadedFaces = new Map<string, Promise<FontFace>>();

function loadFace(family: string, url: string): Promise<FontFace> {
  let pending = loadedFaces.get(family);
  if (!pending) {
    pending = new FontFace(family, `url(${url})`).load().then((face) => {
      document.fonts.add(face);
      return face;
    });
    pending.catch(() => loadedFaces.delete(family));
    loadedFaces.set(family, pending);
  }
  return pending;
}

function FontPreview({ font, text }: { font: Font; text: string }) {
  const family = `font-${font.id}`;
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    let alive = true;
    loadFace(family, fontsApi.fileUrl(font.id))
      .then(() => alive && setState('ready'))
      .catch(() => alive && setState('failed'));
    return () => {
      alive = false;
    };
  }, [family, font.id]);

  if (state === 'failed') return <span className={styles.previewMissing}>Превью недоступно</span>;
  return (
    <span
      className={styles.previewBlock}
      style={state === 'ready' ? { fontFamily: `"${family}"` } : { visibility: 'hidden' }}
    >
      {(text.trim() ? [text] : DEFAULT_SAMPLES).map((line) => (
        <span key={line} className={styles.preview}>
          {line}
        </span>
      ))}
    </span>
  );
}

function FontRow({ font, teams, sample }: { font: Font; teams: string[]; sample: string }) {
  const update = useUpdateFont();
  const remove = useDeleteFont();
  const [name, setName] = useState(font.name);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => setName(font.name), [font.name]);

  const saveName = () => {
    const next = name.trim();
    if (next && next !== font.name) update.mutate({ id: font.id, payload: { name: next } });
    else setName(font.name);
  };

  return (
    <div className={styles.row}>
      <FontPreview font={font} text={sample} />
      <div className={styles.meta}>
        {font.can_edit ? (
          <Input
            size="sm"
            className={styles.nameInput}
            value={name}
            maxLength={200}
            aria-label="Название шрифта"
            onChange={(event) => setName(event.target.value)}
            onBlur={saveName}
            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
          />
        ) : (
          <span className={styles.name}>{font.name}</span>
        )}
        <Pill>{font.format.toUpperCase()}</Pill>
        {font.can_edit ? (
          <Select
            size="sm"
            className={styles.teamSelect}
            value={font.team ?? SHARED_TEAM}
            aria-label="Команда шрифта"
            onChange={(event) => update.mutate({ id: font.id, payload: { team: event.target.value } })}
          >
            <TeamOptions teams={teams} />
          </Select>
        ) : (
          <Pill>{teamLabel(font.team)}</Pill>
        )}
        <span className={styles.muted}>
          {font.author_login ?? 'автор неизвестен'} ·{' '}
          {new Date(font.created_at).toLocaleDateString('ru-RU')} · {sizeLabel(font.size_bytes)}
        </span>
        <span className={styles.actions}>
          <a className={styles.linkBtn} href={fontsApi.fileUrl(font.id, true)} download>
            <Download size={13} strokeWidth={1.6} /> Скачать
          </a>
          {font.can_edit && (
            <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              <Trash2 size={13} strokeWidth={1.6} /> Удалить
            </Button>
          )}
        </span>
      </div>
      {update.isError && <ErrorBanner error={update.error} />}
      <ConfirmDialog
        open={confirming}
        title={`Удалить шрифт «${font.name}»?`}
        body="Файл будет удалён без возможности восстановления."
        confirmLabel="Удалить"
        destructive
        busy={remove.isPending}
        onConfirm={() => remove.mutate(font.id, { onSettled: () => setConfirming(false) })}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

function UploadCard({ teams, defaultTeam }: { teams: string[]; defaultTeam: string }) {
  const inputId = useId();
  const upload = useUploadFonts();
  const [team, setTeam] = useState(defaultTeam);
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState<FontUploadResult | null>(null);

  useEffect(() => setTeam(defaultTeam), [defaultTeam]);

  const send = (list: FileList | null) => {
    const files = list ? Array.from(list) : [];
    if (!files.length || upload.isPending) return;
    setResult(null);
    upload.mutate({ files, team }, { onSuccess: setResult });
  };

  return (
    <Card>
      <CardHead title="Загрузить шрифты" hint=".ttf и .otf, до 20 МБ, можно несколько файлов" />
      <div className={styles.uploadBody}>
        <label className={styles.teamField}>
          <span className={styles.muted}>Для команды</span>
          <Select size="sm" value={team} onChange={(event) => setTeam(event.target.value)}>
            <TeamOptions teams={teams} />
          </Select>
        </label>
        <label
          htmlFor={inputId}
          className={`${styles.drop} ${dragging ? styles.dropActive : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            send(event.dataTransfer.files);
          }}
        >
          <input
            id={inputId}
            type="file"
            multiple
            accept=".ttf,.otf"
            className={styles.hiddenInput}
            onChange={(event) => {
              send(event.target.files);
              event.target.value = '';
            }}
          />
          <Upload size={16} strokeWidth={1.6} aria-hidden="true" />
          <span>{upload.isPending ? 'Загружаем…' : 'Перетащите шрифты или выберите файлы'}</span>
        </label>
        {upload.isError && <ErrorBanner error={upload.error} />}
        {result && (
          <div className={styles.result}>
            {result.created.length > 0 && <div>Загружено: {result.created.length}</div>}
            {result.rejected.map((item) => (
              <div key={item.filename} className={styles.rejected}>
                {item.filename}: {item.reason}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function FontsPage() {
  const { user, can, isAdmin } = useCurrentUser();
  const teamsQuery = useFontTeams();
  const teams = teamsQuery.data ?? [];
  const [filter, setFilter] = useState<string[]>([]);
  const [sample, setSample] = useState('');
  const fonts = useFonts({ teams: isAdmin ? filter : [] });

  const toggle = (team: string) =>
    setFilter((current) =>
      current.includes(team) ? current.filter((t) => t !== team) : [...current, team],
    );
  const defaultTeam = user?.team && teams.includes(user.team) ? user.team : SHARED_TEAM;
  const items = fonts.data?.items ?? [];

  return (
    <div className={styles.page}>
      {can('editor') && <UploadCard teams={teams} defaultTeam={defaultTeam} />}

      <Card>
        <CardHead
          title="Шрифты"
          hint={fonts.data ? `${fonts.data.total}` : undefined}
          actions={
            isAdmin ? (
              <span className={styles.filter} aria-label="Фильтр по командам">
                {[SHARED_TEAM, ...teams].map((team) => (
                  <button
                    key={team}
                    type="button"
                    className={`${styles.chip} ${filter.includes(team) ? styles.chipOn : ''}`}
                    aria-pressed={filter.includes(team)}
                    onClick={() => toggle(team)}
                  >
                    {teamLabel(team)}
                  </button>
                ))}
              </span>
            ) : null
          }
        />
        <div className={styles.sampleBar}>
          <Input
            size="sm"
            value={sample}
            maxLength={200}
            placeholder="Текст превью: впишите свою фразу на любом языке"
            aria-label="Текст превью"
            onChange={(event) => setSample(event.target.value)}
          />
        </div>
        {fonts.isError && <ErrorBanner error={fonts.error} />}
        {fonts.isPending ? (
          <div className={styles.pad}>
            <SkeletonRows rows={3} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Type size={18} strokeWidth={1.6} />}
            title="Шрифтов пока нет"
            description={filter.length ? 'По выбранным командам ничего не найдено.' : undefined}
          />
        ) : (
          <div className={styles.list}>
            {items.map((font) => (
              <FontRow key={font.id} font={font} teams={teams} sample={sample} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
